import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAllowedUserPermissions } from '../../helpers/common';
import { CHILDREN_TAB_SHIPMENTS } from '../../consts/management/users/users';
import { LoaderSpinner } from '../../components/LoaderSpinner';

const PrintLabelSettings = () => {
  const [loading, setLoading] = useState(false);
  const {
    currentUser,
    loading: authLoading,
  } = useSelector((state) => state.auth);

  const userPermissions = currentUser?.permissions || [];
  const { childIdToAccess } = getAllowedUserPermissions({
    permissions: userPermissions,
  });
  const routesAccess = childIdToAccess.get(CHILDREN_TAB_SHIPMENTS);
  const hasEditAccess = routesAccess === "edit";
  const {
    register,
    handleSubmit,
    setValue,
  } = useForm({ defaultValues: { address: '', phoneNumber: '' } });

  useEffect(() => {
    if (!hasEditAccess) return;
    const loadLabelSettingsData = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/v1/metadata/shipping-label-settings');
            if (!response.ok) throw new Error('Failed to load label settings');
            const data = await response.json();
            setValue('address', data.address || "");
            setValue('phoneNumber', data.phoneNumber || "");
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    loadLabelSettingsData();
  }, [hasEditAccess]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
        const response = await fetch('/api/v1/metadata/shipping-label-settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address: data.address, phoneNumber: data.phoneNumber }),
        });
        if (!response.ok) {
            const err = await response.json();
            toast.error(err.error || 'Failed to update label settings', { position: "bottom-right" });
            throw new Error(err.error || 'Failed to label settings');
        };
        toast.success("Label settings updated successfully", { position: "bottom-right" });
    } catch (e) {
        console.error(e);
        toast.error(e, { position: "bottom-right" });
    } finally {
        setLoading(false);
    }
  };

  if (authLoading) return <LoaderSpinner />;
  if (!hasEditAccess) return <div>No access</div>;

  return (
    <main className="w-full h-full p-8">
      <div className="h-full flex flex-col sm:max-w-[900px]">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Print Label Settings</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
            <div>
                <label
                    htmlFor="address"
                    className="block mt-2 mb-1 text-sm font-medium text-gray-900"
                >
                    Shipping address:
                </label>
                <textarea
                    id="address"
                    type="text"
                    {...register("address")}
                    className="flex h-10 no-arrows-by-input rounded-md disabled:bg-gray-100 border border-input bg-background px-3 pl-4 py-2 text-sm min-w-72 min-h-40 resize"
                />
            </div>
            <div>
                <label
                    htmlFor="phoneNumber"
                    className="block mt-2 mb-1 text-sm font-medium text-gray-900"
                >
                    Shipping phone number:
                </label>
                <input
                    id="phoneNumber"
                    type="text"
                    {...register("phoneNumber")}
                    className="flex h-10 no-arrows-by-input rounded-md disabled:bg-gray-100 border border-input bg-background px-3 pl-4 py-2 text-sm w-48"
                />
            </div>
            <div className="mt-5 sm:mt-4">
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full disabled:bg-slate-200 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:w-auto sm:text-sm"
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </form>
      </div>
    </main>
  );
};

export default PrintLabelSettings;
